import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useState,
  useContext,
  useEffect,
} from 'react';
import { BiSearch } from 'react-icons/bi';
import { InputComponent } from 'ReusableComponents/Input/InputComponent';
import { CarType, CourierJson } from 'types/bundleTypes';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { CheckboxComponent } from 'ReusableComponents/Checkbox/CheckboxComponent';
import { capitalize } from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import SortAscIcon from 'assets/svg/SortAscIcon';
import SortDescIcon from 'assets/svg/SortDescIcon';
import SortNeutralIcon from 'assets/svg/SortNeutralIcon';
import {
  stringComparator,
  numberComparator,
  Comparator,
  dateComparator,
} from '../utils';
import { RoleIcons } from '../smallerComponents';
import './CourierModals.scss';
import { TooltipComponent } from '../../../ReusableComponents/Tooltip/TooltipComponent';
import TablePagination from '@material-ui/core/TablePagination';
import clsx from 'clsx';
import { ShiftPlannerContext } from '../ShiftPlanner';
import { RadioComponent } from '../../../ReusableComponents/Radio/RadioComponent';
import StarIcon from 'assets/svg/StarIcon';
import { Region } from '../../../types/orderTypes';

export interface CourierLists {
  [groupName: string]: CourierJson[];
}

export enum RoleFilterType {
  ALL,
  COURIER,
  SORTER,
}

type OrderingColumn =
  | 'fullName'
  | 'companyName'
  | 'shiftsCount'
  | 'dateCreated'
  | 'etaOffsetSeconds'
  | 'efficiency';

enum SortType {
  ASCENDING,
  DESCENDING,
  NEUTRAL,
}

export const SortingIcon: FC<{ sortType: SortType }> = ({ sortType }) => {
  switch (sortType) {
    case SortType.ASCENDING:
      return <SortAscIcon />;
    case SortType.DESCENDING:
      return <SortDescIcon />;
    case SortType.NEUTRAL:
      return <SortNeutralIcon />;
  }
};

export const CourierFilterSection: FC<{
  selectedVehicleTypes: Set<CarType>;
  setSelectedVehicleTypes: Dispatch<SetStateAction<Set<CarType>>>;
  courierSearch: string;
  setCourierSearch: Dispatch<SetStateAction<string>>;
  checkedValue: RoleFilterType;
  setCheckedValue: Dispatch<SetStateAction<RoleFilterType>>;
}> = ({
  selectedVehicleTypes,
  setSelectedVehicleTypes,
  courierSearch,
  setCourierSearch,
  checkedValue,
  setCheckedValue,
}) => {
  const toggleVehicleType = (vehicleType: CarType) => {
    const selectedVehicleTypesCopy = new Set(selectedVehicleTypes);
    selectedVehicleTypes.has(vehicleType)
      ? selectedVehicleTypesCopy.delete(vehicleType)
      : selectedVehicleTypesCopy.add(vehicleType);

    setSelectedVehicleTypes(selectedVehicleTypesCopy);
  };

  const setCourierSearchCallback = (e: ChangeEvent<HTMLInputElement>) =>
    setCourierSearch(e.currentTarget.value);

  return (
    <div className="courierFilterSection">
      <div className="courierFilterSection__top">
        <p>Search:</p>
        <div className="courierFilterSection__top__search">
          <InputComponent
            className="courierFilterSection__top__search__input"
            margin="none"
            id="shift-modal-couriersearch"
            label="Search courier"
            name="courier-search"
            value={courierSearch}
            onChange={setCourierSearchCallback}
            variant="outlined"
          />
          <ButtonComponent
            className="courierFilterSection__top__search__button"
            style={{ backgroundColor: '#666666' }}
          >
            <BiSearch />
          </ButtonComponent>
        </div>
      </div>
      <div className="courierFilterSection__bottom">
        <div className="courierFilterSection__bottom__vehicleFilter">
          <p>Filter vehicle type:</p>
          <div className="courierFilterSection__bottom__vehicleFilter__list">
            {Object.values(CarType)
              .filter(type => type !== CarType.null)
              .map(carType => (
                <CheckboxComponent
                  key={carType}
                  checkBoxClassName="courierFilterSection__bottom__vehicleFilter__list__checkbox"
                  labelClassName="courierFilterSection__bottom__vehicleFilter__list__label"
                  checked={selectedVehicleTypes.has(carType)}
                  name=""
                  label={capitalize(carType.replace('_', ' '))}
                  handleChange={() => toggleVehicleType(carType)}
                />
              ))}
          </div>
        </div>
        <div className="courierFilterSection__bottom__workTypeFilter">
          <p className="courierFilterSection__bottom__workTypeFilter__description">
            Filter job type:
          </p>
          <div className="courierFilterSection__bottom__workTypeFilter__radios">
            <RadioComponent
              id="courierPicker-1"
              name="courierPicker-radio"
              label={{ node: 'All' }}
              checked={checkedValue === RoleFilterType.ALL}
              value={RoleFilterType.ALL}
              onChange={() => setCheckedValue(RoleFilterType.ALL)}
            />
            <RadioComponent
              id="courierPicker-2"
              name="courierPicker-radio"
              label={{ node: 'Courier' }}
              checked={checkedValue === RoleFilterType.COURIER}
              value={RoleFilterType.COURIER}
              onChange={() => setCheckedValue(RoleFilterType.COURIER)}
            />
            <RadioComponent
              id="courierPicker-3"
              name="courierPicker-radio"
              label={{ node: 'Sorter' }}
              checked={checkedValue === RoleFilterType.SORTER}
              value={RoleFilterType.SORTER}
              onChange={() => setCheckedValue(RoleFilterType.SORTER)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const CourierListSection: FC<{
  selectedCouriers: Set<number>;
  setSelectedCouriers: Dispatch<SetStateAction<Set<number>>>;
  selectedWorkType?: string;
  setWorkAssignedToCouriers?: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
  hideCurrentPreviousColumns?: boolean;
  courierLists: CourierLists;
  courierSelectionLimit?: number;
  showPerformanceMeasures?: boolean;
  couriersEfficiency?: Record<string, number>;
  allowAllCouriersSelection: boolean;
}> = ({
  courierLists,
  courierSelectionLimit,
  allowAllCouriersSelection,
  couriersEfficiency,
  ...rest
}) => {
  const isWishGroup = (groupName: string) => groupName.toLowerCase() === 'wish';
  return (
    <>
      {Object.entries(courierLists).map(([groupName, couriers]) => (
        <div key={groupName} className="courierListSection">
          {groupName && (
            <p
              className={clsx(
                isWishGroup(groupName)
                  ? 'courierListSection__yellowTitle'
                  : 'courierListSection__grayTitle'
              )}
            >
              {groupName}
            </p>
          )}
          <div className="courierListSection__groups">
            <CourierTable
              couriers={couriers}
              courierSelectionLimit={courierSelectionLimit}
              allowAllCouriersSelection={allowAllCouriersSelection}
              couriersEfficiency={couriersEfficiency}
              {...rest}
            />
          </div>
        </div>
      ))}
    </>
  );
};

const CourierTable: FC<{
  selectedCouriers: Set<number>;
  setSelectedCouriers: Dispatch<SetStateAction<Set<number>>> | null;
  selectedWorkType?: string;
  setWorkAssignedToCouriers?: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
  couriers: CourierJson[];
  hideCurrentPreviousColumns?: boolean;
  courierSelectionLimit?: number;
  showPerformanceMeasures?: boolean;
  couriersEfficiency?: Record<string, number>;
  allowAllCouriersSelection: boolean;
}> = ({
  selectedCouriers,
  setSelectedCouriers,
  selectedWorkType,
  setWorkAssignedToCouriers,
  couriers,
  hideCurrentPreviousColumns,
  courierSelectionLimit,
  showPerformanceMeasures,
  couriersEfficiency,
  allowAllCouriersSelection,
}) => {
  const { shiftCount, region } = useContext(ShiftPlannerContext);
  const [order, setOrder] = useState<SortType>(SortType.ASCENDING);
  const [orderBy, setOrderBy] = useState<OrderingColumn | null>(null);
  const [couriersToDisplay, setCouriersToDisplay] =
    useState<CourierJson[]>(couriers);

  const [page, setPage] = useState(0);
  const itemsPerPage = 10;

  const disableSelectionCheckbox =
    courierSelectionLimit && selectedCouriers.size >= courierSelectionLimit;

  const updateSort = (column: OrderingColumn, comparator: Comparator<any>) => {
    const newOrder =
      orderBy === column && order === SortType.ASCENDING
        ? SortType.DESCENDING
        : SortType.ASCENDING;

    const couriersCopy = [...couriersToDisplay];
    couriersCopy.sort((a: CourierJson, b: CourierJson) => {
      const valuesToSortBy =
        column === 'efficiency'
          ? {
              a: couriersEfficiency[a.id] || 1,
              b: couriersEfficiency[b.id] || 1,
            }
          : { a: a[column], b: b[column] };
      return newOrder === SortType.ASCENDING
        ? comparator(valuesToSortBy.a, valuesToSortBy.b)
        : -comparator(valuesToSortBy.a, valuesToSortBy.b);
    });

    setOrderBy(column);
    setOrder(newOrder);
    setCouriersToDisplay(couriersCopy);
  };

  const addCourierWorkType = (courierId: string) => {
    if (setWorkAssignedToCouriers) {
      setWorkAssignedToCouriers(currentCourierWork => {
        if (currentCourierWork[courierId]) {
          let courierWorkCopy = currentCourierWork[courierId];
          courierWorkCopy.includes(selectedWorkType)
            ? (courierWorkCopy = courierWorkCopy.filter(
                workType => workType !== selectedWorkType
              ))
            : courierWorkCopy.push(selectedWorkType);
          return { ...currentCourierWork, [courierId]: courierWorkCopy };
        }
        return { ...currentCourierWork, [courierId]: [selectedWorkType] };
      });
    }
  };

  const pagination = couriers?.length ? (
    <TablePagination
      count={couriers?.length}
      page={page}
      onPageChange={(_, value: number) => setPage(value)}
      rowsPerPage={itemsPerPage}
      rowsPerPageOptions={[]}
    />
  ) : null;

  const toggleSelectedCourier = (courierId: number) => {
    setSelectedCouriers(prevSelectedIds => {
      const selectedIdsCopy = new Set(prevSelectedIds);
      if (selectedIdsCopy.has(courierId)) {
        selectedIdsCopy.delete(courierId);
      } else {
        if (!disableSelectionCheckbox) {
          selectedIdsCopy.add(courierId);
        }
      }
      return selectedIdsCopy;
    });
  };

  const handleSelectAllCouriers = (e: ChangeEvent<HTMLInputElement>) => {
    const isSelectAllChecked = e.currentTarget.checked;
    setSelectedCouriers(prevSelectedIds => {
      const selectedIdsCopy = new Set(prevSelectedIds);
      isSelectAllChecked
        ? couriersToDisplay.forEach(courier => selectedIdsCopy.add(courier.id))
        : couriersToDisplay.forEach(courier =>
            selectedIdsCopy.delete(courier.id)
          );
      return selectedIdsCopy;
    });
  };

  const courierEfficiencyRating = (courierId: number) => {
    const courierRating = couriersEfficiency[courierId] ?? 1;
    return (
      <div className="starRating">
        {[...Array(5)].map((star, index) => (
          <StarIcon
            key={`${courierId}_${courierRating}_${index}`}
            className={
              index < courierRating
                ? 'starRating__darkGrey'
                : 'starRating__lightGrey'
            }
          />
        ))}
      </div>
    );
  };

  const allCouriersSelected =
    selectedCouriers.size > 0 &&
    couriersToDisplay.length > 0 &&
    couriersToDisplay.every(courier => selectedCouriers.has(courier.id));

  useEffect(() => {
    setPage(0);
    setCouriersToDisplay(couriers);
  }, [couriers]);

  return (
    <TableContainer className="courierGroup">
      <Table className="courierGroup">
        <TableHead>
          <TableRow className="courierGroup__rowHeader">
            <TableCell className="courierGroup__col1">
              {allowAllCouriersSelection && (
                <CheckboxComponent
                  disabled={
                    !!courierSelectionLimit && !allowAllCouriersSelection
                  }
                  checked={allCouriersSelected}
                  handleChange={handleSelectAllCouriers}
                  name="select-visible-couriers"
                />
              )}
            </TableCell>
            <TableCell className="courierGroup__aligned-left">Roles</TableCell>
            <TableCell onClick={() => updateSort('fullName', stringComparator)}>
              <div className="courierGroup__sorted">
                <span>Names</span>
                <SortingIcon
                  sortType={orderBy === 'fullName' ? order : SortType.NEUTRAL}
                />
              </div>
            </TableCell>
            <TableCell
              className="courierGroup__sorted"
              onClick={() => updateSort('companyName', stringComparator)}
            >
              <div className="courierGroup__sorted">
                <span>{'Company'}</span>
                <SortingIcon
                  sortType={
                    orderBy === 'companyName' ? order : SortType.NEUTRAL
                  }
                />
              </div>
            </TableCell>
            <TableCell
              className="courierGroup__sorted"
              onClick={() => updateSort('dateCreated', dateComparator)}
            >
              <div className="courierGroup__sorted">
                <span>{'Created'}</span>
                <SortingIcon
                  sortType={
                    orderBy === 'dateCreated' ? order : SortType.NEUTRAL
                  }
                />
              </div>
            </TableCell>
            <TableCell
              className="courierGroup__sorted"
              onClick={() => updateSort('shiftsCount', numberComparator)}
            >
              <div className="courierGroup__sorted">
                <span>{'Shifts taken'}</span>
                <SortingIcon
                  sortType={
                    orderBy === 'shiftsCount' ? order : SortType.NEUTRAL
                  }
                />
              </div>
            </TableCell>
            {showPerformanceMeasures && region !== Region.OSLO && (
              <TableCell
                onClick={() => updateSort('etaOffsetSeconds', numberComparator)}
              >
                <div className="courierGroup__sorted">
                  <span>ETA</span>
                  <SortingIcon
                    sortType={
                      orderBy === 'etaOffsetSeconds' ? order : SortType.NEUTRAL
                    }
                  />
                </div>
              </TableCell>
            )}
            {showPerformanceMeasures && region === Region.OSLO && (
              <TableCell
                onClick={() => updateSort('efficiency', numberComparator)}
              >
                <div className="courierGroup__sorted">
                  <span>Efficiency</span>
                  <SortingIcon
                    sortType={
                      orderBy === 'efficiency' ? order : SortType.NEUTRAL
                    }
                  />
                </div>
              </TableCell>
            )}
            {!hideCurrentPreviousColumns && (
              <>
                <TableCell className="courierGroup__centered">
                  <TooltipComponent
                    id="courier-work-counts-current"
                    title="Booked / Requested / Declined / Canceled / DD"
                  >
                    <span>Current</span>
                  </TooltipComponent>
                </TableCell>
                <TableCell className="courierGroup__centered">
                  <TooltipComponent
                    id="courier-work-counts-previous"
                    title="Booked / Requested / Declined / Canceled / DD"
                  >
                    Previous
                  </TooltipComponent>
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {couriersToDisplay
            .slice(page * itemsPerPage, (page + 1) * itemsPerPage)
            .map(courier => (
              <TableRow
                key={courier.id}
                className={clsx(
                  'courierGroup__row',
                  selectedCouriers.has(courier.id)
                    ? 'courierGroup__row__selected'
                    : 'courierGroup__row__unselected'
                )}
                onClick={() => {
                  toggleSelectedCourier(courier.id);
                  addCourierWorkType(courier.id.toString());
                }}
                data-courierid={courier.id}
              >
                <TableCell className="courierGroup__col1">
                  <CheckboxComponent
                    disabled={
                      disableSelectionCheckbox &&
                      !selectedCouriers.has(courier.id)
                    }
                    checked={selectedCouriers.has(courier.id)}
                    name={courier.id.toString()}
                  />
                </TableCell>
                <TableCell className="courierGroup__roles">
                  <RoleIcons
                    className="courierGroup__roles__icons"
                    vehicleType={courier.isCourier && courier.travelMode}
                    isElectric={courier.carType === CarType.ELECTRIC}
                    isScanner={courier.isSorter}
                  />
                </TableCell>
                <TableCell className="courierGroup__name">
                  {courier.fullName.length > 22 ? (
                    <TooltipComponent
                      className="custom-tooltip-container"
                      id={courier.fullName}
                      title={courier.fullName}
                      placement="top-start"
                    >
                      <span className="courier-tooltip">
                        {courier.fullName}
                      </span>
                    </TooltipComponent>
                  ) : (
                    <span>{courier.fullName}</span>
                  )}
                </TableCell>
                <TableCell className="courierGroup__text">
                  {courier.companyName?.length > 10 ? (
                    <TooltipComponent
                      className="custom-tooltip-container"
                      id={courier.companyName}
                      title={courier.companyName}
                      placement="top-start"
                    >
                      <span className="courier-company-tooltip">
                        {courier.companyName}
                      </span>
                    </TooltipComponent>
                  ) : (
                    <span>{courier.companyName}</span>
                  )}
                </TableCell>
                <TableCell className="courierGroup__date">
                  <span>{courier.dateCreated}</span>
                </TableCell>
                <TableCell className="courierGroup__text">
                  <span>{courier.shiftsCount}</span>
                </TableCell>
                {showPerformanceMeasures && region !== Region.OSLO && (
                  <TableCell
                    className={clsx(
                      'courierGroup__text',
                      'courierGroup__aligned-left'
                    )}
                  >
                    {courier.etaOffsetSeconds}
                  </TableCell>
                )}
                {showPerformanceMeasures && region === Region.OSLO && (
                  <TableCell
                    className={clsx(
                      'courierGroup__text',
                      'courierGroup__aligned-left'
                    )}
                  >
                    {courierEfficiencyRating(courier.id)}
                  </TableCell>
                )}
                {!hideCurrentPreviousColumns && (
                  <>
                    <TableCell
                      className={clsx(
                        'courierGroup__text',
                        'courierGroup__centered',
                        'courierGroup__workCounts'
                      )}
                    >
                      <span>
                        {shiftCount.currentWeek[courier?.id]?.booked ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.currentWeek[courier?.id]?.requested ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.currentWeek[courier?.id]?.declined ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.currentWeek[courier?.id]?.canceled ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.currentWeek[courier?.id]
                          ?.dispatchDeclined ?? 0}
                      </span>
                    </TableCell>
                    <TableCell
                      className={clsx(
                        'courierGroup__text',
                        'courierGroup__centered',
                        'courierGroup__workCounts'
                      )}
                    >
                      <span>
                        {shiftCount.previousWeek[courier?.id]?.booked ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.previousWeek[courier?.id]?.requested ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.previousWeek[courier?.id]?.declined ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.previousWeek[courier?.id]?.canceled ?? 0}
                      </span>
                      {' / '}
                      <span>
                        {shiftCount.currentWeek[courier?.id]
                          ?.dispatchDeclined ?? 0}
                      </span>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {pagination}
    </TableContainer>
  );
};
